import React, { useCallback, useEffect, useState } from 'react';
import { Box, HStack, IconButton, Menu, MenuButton, MenuList, MenuOptionGroup, MenuItemOption, createIcon, useColorMode } from '@chakra-ui/react';
import { MoonIcon } from '@chakra-ui/icons';

type Preference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'iland.theme-preference';

const SunIcon = createIcon({
  displayName: 'ThemeSunIcon',
  viewBox: '0 0 24 24',
  path: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2m0 16v2M2 12h2m16 0h2M4.93 4.93l1.42 1.42m11.3 11.3 1.42 1.42M4.93 19.07l1.42-1.42m11.3-11.3 1.42-1.42" />
    </g>
  ),
});

const MonitorIcon = createIcon({
  displayName: 'MonitorIcon',
  viewBox: '0 0 24 24',
  path: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </g>
  ),
});

const readPreference = (): Preference | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'system' || value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
};

const systemMode = (): 'light' | 'dark' =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const options: { value: Preference; label: string; icon: React.ReactElement }[] = [
  { value: 'system', label: 'Use system theme', icon: <MonitorIcon boxSize={3.5} /> },
  { value: 'light', label: 'Light theme', icon: <SunIcon boxSize={4} /> },
  { value: 'dark', label: 'Dark theme', icon: <MoonIcon boxSize={3} /> },
];

interface Props {
  // Over the home hero the header is transparent, so the control is drawn in
  // light-on-dark regardless of the page theme.
  overMedia?: boolean;
}

// Chakra only remembers light or dark, so "follow the system" is kept here and
// re-applied whenever the operating system switches.
const ThemeSwitcher: React.FC<Props> = ({ overMedia = false }) => {
  const { colorMode, setColorMode } = useColorMode();
  const [preference, setPreference] = useState<Preference>(() => readPreference() ?? 'system');

  useEffect(() => {
    setColorMode(preference === 'system' ? systemMode() : preference);
    if (preference !== 'system') return undefined;

    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return undefined;
    const follow = () => setColorMode(systemMode());
    media.addEventListener('change', follow);
    return () => media.removeEventListener('change', follow);
  }, [preference, setColorMode]);

  const choose = useCallback(
    (value: Preference) => {
      setPreference(value);
      try {
        window.localStorage.setItem(STORAGE_KEY, value);
      } catch {
        // The choice still applies for this visit.
      }
      if (value !== 'system') setColorMode(value);
    },
    [setColorMode]
  );

  const dark = overMedia || colorMode === 'dark';
  const borderColor = overMedia ? 'rgba(255,255,255,0.32)' : dark ? 'whiteAlpha.200' : 'blackAlpha.200';
  const controlBg = dark ? (overMedia ? 'rgba(7,19,33,0.68)' : 'whiteAlpha.100') : 'white';
  const controlColor = dark ? 'white' : 'gray.800';
  const activeBg = dark ? 'whiteAlpha.200' : 'blackAlpha.100';
  const activeOption = options.find(option => option.value === preference)!;

  return (
    <>
      {/* Only the narrowest phones need a menu; every theme remains one tap away. */}
      <Box display="none" sx={{ '@media (max-width: 339px)': { display: 'block' } }}>
        <Menu placement="bottom-end">
          <MenuButton
            as={IconButton}
            aria-label={`Theme: ${preference}. Choose theme`}
            icon={activeOption.icon}
            minW="28px"
            w="28px"
            h="28px"
            rounded="full"
            color={controlColor}
            bg={controlBg}
            border="1px solid"
            borderColor={borderColor}
            _hover={{ bg: activeBg }}
            _active={{ bg: activeBg }}
          />
          <MenuList minW="160px" zIndex={1100}>
            <MenuOptionGroup title="Theme" type="radio" value={preference} onChange={value => choose(value as Preference)}>
              {options.map(option => (
                <MenuItemOption key={option.value} value={option.value}>
                  <HStack spacing={2}>{option.icon}<Box>{option.value === 'system' ? 'System' : option.value === 'light' ? 'Light' : 'Dark'}</Box></HStack>
                </MenuItemOption>
              ))}
            </MenuOptionGroup>
          </MenuList>
        </Menu>
      </Box>
      <HStack
        role="radiogroup"
        aria-label="Theme"
        spacing={0.5}
        p={{ base: '2px', sm: '3px' }}
        display="flex"
        borderRadius="full"
        border="1px solid"
        borderColor={borderColor}
        color={controlColor}
        bg={controlBg}
        backdropFilter={overMedia ? 'blur(8px)' : undefined}
        sx={{ '@media (max-width: 339px)': { display: 'none' } }}
      >
        {options.map((option) => {
          const selected = preference === option.value;
          return (
            <Box
              key={option.value}
              as="button"
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={option.label}
              title={option.label}
              onClick={() => choose(option.value)}
              display="flex"
              alignItems="center"
              justifyContent="center"
              w={{ base: '24px', sm: '28px' }}
              h="24px"
              flexShrink={0}
              borderRadius="full"
              color={selected ? controlColor : dark ? 'whiteAlpha.800' : 'gray.600'}
              bg={selected ? activeBg : 'transparent'}
              transition="background 150ms ease, color 150ms ease"
              _hover={{ color: controlColor }}
              _focusVisible={{ outline: '2px solid', outlineColor: 'orange.400' }}
            >
              {option.icon}
            </Box>
          );
        })}
      </HStack>
    </>
  );
};

export default ThemeSwitcher;
